import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import { useToast } from '../../../../hooks/toast';
import { currencyMask } from '../../../../utils/inputMasks';
import {
  AdminGymFeaturesService,
  IAdminGymFeature,
  IAdminGymFeaturesDetail,
} from '../../../../services/AdminGymFeaturesService';
import { colors } from '../../../../styles/tokens';

import {
  AddAddonForm,
  AddAddonLabel,
  BackButton,
  Container,
  EmptyDetail,
  FeatureInfo,
  FeatureList,
  FeatureMeta,
  FeatureName,
  FeatureRow,
  GroupSection,
  GroupTitle,
  Header,
  IconButton,
  MaskedFieldGroup,
  MaskedFieldUnit,
  MaskedInput,
  OriginBadge,
  PackagesTable,
  PackagesTableHead,
  PackagesTableRow,
  Panel,
  PlanIncludedBadge,
  PageSubtitle,
  PageTitle,
  SummaryBadge,
  SummaryBox,
  SummaryBoxLabel,
  SummaryBoxValue,
  SummaryCard,
  SummaryDescription,
  SummaryEquation,
  SummaryGymName,
  SummaryHead,
  SummaryOperator,
  SummaryTitleRow,
  TabButton,
  TabRow,
  ToggleSwitch,
  TrialWarning,
  UnlimitedLabel,
  ValueCell,
} from './styles';

// Mesma distinção de admin/pages/plans -- sem coluna de tipo em
// `features`, então isso é hardcoded aqui também (só muda se um
// package numérico novo for criado).
const NUMERIC_FEATURE_CODES = ['users', 'exercises', 'news'];

interface IFeatureGroup {
  code: string;
  label: string;
  icon: string;
  features: IAdminGymFeature[];
}

// Agrupamento da aba Permissões -- vem de `feature.category`
// (features.category_id no banco, seedado em
// sql/15_seed_feature_categories.sql), não é mais mapa hardcoded aqui.
// `booleanFeatures` já chega ordenado por category.order
// (PackagesRepository.findAll), então a ordem de inserção no Map já
// reflete a ordem certa de exibição. Feature sem categoria (nenhuma
// hoje) fica de fora da aba, mesmo racional de groupCatalogByCategory
// pra permissões.
function groupFeaturesByCategory(
  features: IAdminGymFeature[],
): IFeatureGroup[] {
  const groups = new Map<string, IFeatureGroup>();

  features.forEach(feature => {
    if (!feature.category) return;

    const { category } = feature;
    const existing = groups.get(category.code);

    if (existing) {
      existing.features.push(feature);
      return;
    }

    groups.set(category.code, {
      code: category.code,
      label: category.label,
      icon: category.icon,
      features: [feature],
    });
  });

  return Array.from(groups.values());
}

const formatPrice = (value: number): string =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const onlyDigits = (value: string): string => value.replace(/\D/g, '');

interface INewAddonDraft {
  value: string;
  price: string;
  unlimited: boolean;
}

const EMPTY_ADDON_DRAFT: INewAddonDraft = {
  value: '',
  price: '',
  unlimited: false,
};

// Pré-preenche o form de "adicionar addon"/"ligar com preço" com a
// referência do pacote (`features.default_addon_value/price`), quando
// existe -- puramente sugestão de UI, `null` = sem padrão definido
// ainda pra essa feature.
function getDefaultDraft(feature: IAdminGymFeature): INewAddonDraft {
  return {
    value:
      feature.default_addon_value !== null
        ? String(feature.default_addon_value)
        : '',
    price:
      feature.default_addon_price !== null
        ? currencyMask(String(Math.round(feature.default_addon_price * 100)))
        : '',
    unlimited: false,
  };
}

const GymFeatures: React.FC = () => {
  const { gym_id } = useParams<{ gym_id: string }>();
  const navigate = useNavigate();
  const adminGymFeaturesService = new AdminGymFeaturesService();
  const { addToast } = useToast();

  const [detail, setDetail] = useState<IAdminGymFeaturesDetail>();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'permissions' | 'packages'>(
    'permissions',
  );
  const [savingFeatureId, setSavingFeatureId] = useState<string | null>(null);
  const [removingAddonId, setRemovingAddonId] = useState<string | null>(null);

  // Rascunho do form "adicionar addon" (aba Pacotes), um por feature.
  const [newAddonDrafts, setNewAddonDrafts] = useState<
    Record<string, INewAddonDraft>
  >({});

  // Preço digitado antes de confirmar ligar um boolean (aba
  // Permissões) -- só existe enquanto o form de confirmação está
  // aberto, ver `pendingGrant`.
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});
  const [pendingGrant, setPendingGrant] = useState<Record<string, boolean>>(
    {},
  );

  const fetchDetail = useCallback(() => {
    if (!gym_id) return;

    setLoading(true);
    adminGymFeaturesService
      .getGymFeatures(gym_id)
      .then(response => {
        setDetail(response.data);
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Não foi possível carregar a academia.',
        });
        navigate('/admin/gyms');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gym_id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleToggleBoolean = useCallback(
    async (feature_id: string, checked: boolean) => {
      if (!gym_id) return;

      if (checked) {
        // Ligar pede preço antes -- só abre o form de confirmação,
        // não chama a API ainda (ver botão "Salvar" mais abaixo). Já
        // grava o preço padrão (se existir) direto no estado -- não só
        // na renderização -- senão confirmar sem tocar no campo
        // submeteria 0 em vez do valor que está sendo exibido.
        const feature = detail?.features.find(
          f => f.feature_id === feature_id,
        );
        setPriceDrafts(current => ({
          ...current,
          [feature_id]: feature ? getDefaultDraft(feature).price : '',
        }));
        setPendingGrant(current => ({ ...current, [feature_id]: true }));
        return;
      }

      setSavingFeatureId(feature_id);
      try {
        await adminGymFeaturesService.revokeFeature(gym_id, feature_id);
        fetchDetail();
      } catch (err) {
        addToast({ type: 'error', title: 'Não foi possível salvar.' });
      } finally {
        setSavingFeatureId(null);
      }
    },
    [gym_id, detail, fetchDetail, addToast],
  );

  const handleConfirmBooleanGrant = useCallback(
    async (feature_id: string) => {
      if (!gym_id) return;

      const price = Number(currencyMask(priceDrafts[feature_id], true)) || 0;

      setSavingFeatureId(feature_id);
      try {
        await adminGymFeaturesService.grantFeature(gym_id, feature_id, 1, price);
        setPendingGrant(current => ({ ...current, [feature_id]: false }));
        setPriceDrafts(current => ({ ...current, [feature_id]: '' }));
        fetchDetail();
      } catch (err) {
        addToast({ type: 'error', title: 'Não foi possível salvar.' });
      } finally {
        setSavingFeatureId(null);
      }
    },
    [gym_id, priceDrafts, fetchDetail, addToast],
  );

  const handleAddAddon = useCallback(
    async (feature_id: string) => {
      if (!gym_id) return;

      const feature = detail?.features.find(f => f.feature_id === feature_id);
      const fallbackDraft = feature
        ? getDefaultDraft(feature)
        : EMPTY_ADDON_DRAFT;
      // Cai no mesmo padrão que a renderização mostra quando o admin
      // nunca tocou nos campos -- senão confirmar um addon com o valor
      // padrão já visível nos inputs submeteria 0 em vez dele.
      const draft = newAddonDrafts[feature_id] || fallbackDraft;
      const value = draft.unlimited ? null : Number(draft.value) || 0;
      const price = Number(currencyMask(draft.price, true)) || 0;

      setSavingFeatureId(feature_id);
      try {
        await adminGymFeaturesService.addAddon(gym_id, feature_id, value, price);
        setNewAddonDrafts(current => ({
          ...current,
          [feature_id]: fallbackDraft,
        }));
        fetchDetail();
        addToast({ type: 'success', title: 'Addon adicionado.' });
      } catch (err) {
        addToast({ type: 'error', title: 'Não foi possível salvar.' });
      } finally {
        setSavingFeatureId(null);
      }
    },
    [gym_id, detail, newAddonDrafts, fetchDetail, addToast],
  );

  const handleRemoveAddon = useCallback(
    async (addon_id: string) => {
      if (!gym_id) return;

      setRemovingAddonId(addon_id);
      try {
        await adminGymFeaturesService.removeAddon(gym_id, addon_id);
        fetchDetail();
      } catch (err) {
        addToast({ type: 'error', title: 'Não foi possível remover.' });
      } finally {
        setRemovingAddonId(null);
      }
    },
    [gym_id, fetchDetail, addToast],
  );

  const numericFeatures =
    detail?.features.filter(feature =>
      NUMERIC_FEATURE_CODES.includes(feature.code),
    ) || [];
  const booleanFeatures =
    detail?.features.filter(
      feature => !NUMERIC_FEATURE_CODES.includes(feature.code),
    ) || [];

  const totalAddonsCount =
    detail?.features.reduce((sum, feature) => sum + feature.addons.length, 0) ||
    0;

  return (
    <Painel loading={loading}>
      <Container>
        <BackButton
          type="button"
          onClick={() => navigate(`/admin/gyms/${gym_id}`)}
        >
          <Icon name="arrow_back" size={18} color={colors.onSurfaceVariant} />
          {detail?.gym.name || 'Academia'}
        </BackButton>

        <Header>
          <div>
            <PageTitle>Features & addons</PageTitle>
            <PageSubtitle>
              Conceda ou revogue addons específicos desta academia, além do
              que o plano dela já inclui por padrão
            </PageSubtitle>
          </div>
        </Header>

        {!detail && <EmptyDetail>Carregando...</EmptyDetail>}

        {detail && (
          <>
            <SummaryCard>
              <SummaryHead>
                <SummaryTitleRow>
                  <SummaryGymName>{detail.gym.name}</SummaryGymName>
                  <SummaryBadge>{detail.gym.domain}</SummaryBadge>
                  <SummaryBadge>
                    {totalAddonsCount}{' '}
                    {totalAddonsCount === 1
                      ? 'addon ativo'
                      : 'addons ativos'}
                  </SummaryBadge>
                </SummaryTitleRow>
                <SummaryDescription>
                  Plano atual: <strong>{detail.plan_name}</strong>. Múltiplos
                  pacotes do mesmo tipo somam cotas cumulativas sobre a
                  franquia base -- gerencie os pacotes unitários aqui ou edite
                  a franquia nativa em{' '}
                  <button type="button" onClick={() => navigate('/admin/plans')}>
                    Gestão de planos
                  </button>
                  .
                </SummaryDescription>
              </SummaryHead>

              <SummaryEquation>
                <SummaryBox>
                  <SummaryBoxLabel>Plano base</SummaryBoxLabel>
                  <SummaryBoxValue>
                    {formatPrice(detail.plan_price)}
                  </SummaryBoxValue>
                </SummaryBox>
                <SummaryOperator>+</SummaryOperator>
                <SummaryBox>
                  <SummaryBoxLabel>Addons ({totalAddonsCount})</SummaryBoxLabel>
                  <SummaryBoxValue>
                    +{formatPrice(detail.addons_price_total)}
                  </SummaryBoxValue>
                </SummaryBox>
                <SummaryOperator>=</SummaryOperator>
                <SummaryBox emphasis>
                  <SummaryBoxLabel>Total mensal</SummaryBoxLabel>
                  <SummaryBoxValue emphasis>
                    {formatPrice(detail.total_price)}
                  </SummaryBoxValue>
                </SummaryBox>
              </SummaryEquation>
            </SummaryCard>

            {!detail.can_grant_addons && (
              <TrialWarning>
                <Icon name="info" size={16} color={colors.onSurface} />
                Academia ainda em período de teste, sem assinatura própria --
                não é possível conceder addon até ela escolher um plano pago.
              </TrialWarning>
            )}

            <TabRow>
              <TabButton
                type="button"
                active={activeTab === 'permissions'}
                onClick={() => setActiveTab('permissions')}
              >
                Permissões
              </TabButton>
              <TabButton
                type="button"
                active={activeTab === 'packages'}
                onClick={() => setActiveTab('packages')}
              >
                Pacotes
              </TabButton>
            </TabRow>

            {activeTab === 'permissions' && (
              <Panel>
                {groupFeaturesByCategory(booleanFeatures).map(group => (
                  <GroupSection key={group.code}>
                    <GroupTitle>
                      <Icon
                        name={group.icon}
                        size={14}
                        color={colors.tertiaryContainer}
                      />
                      {group.label}
                    </GroupTitle>
                    <FeatureList>
                      {group.features.map(feature => {
                          const granted =
                            !!feature.effective_value &&
                            feature.effective_value > 0;
                          const plannedByPlan =
                            !!feature.plan_value && feature.plan_value > 0;
                          const showConfirm =
                            !!pendingGrant[feature.feature_id];

                          return (
                            <FeatureRow key={feature.feature_id}>
                              <FeatureInfo>
                                <FeatureName>
                                  {feature.nome}
                                  {plannedByPlan && (
                                    <PlanIncludedBadge>
                                      Incluído no plano
                                    </PlanIncludedBadge>
                                  )}
                                </FeatureName>
                                <FeatureMeta>
                                  <code>{feature.code}</code> · Efetivo:{' '}
                                  {granted ? 'Liberado' : 'Bloqueado'}
                                  {granted && feature.addons[0]
                                    ? ` · ${formatPrice(
                                        feature.addons[0].price,
                                      )}/mês`
                                    : feature.default_addon_price !== null &&
                                      ` · ref.: ${formatPrice(
                                        feature.default_addon_price,
                                      )}/mês`}
                                </FeatureMeta>
                              </FeatureInfo>

                              {showConfirm ? (
                                <ValueCell>
                                  <AddAddonLabel>
                                    Preço/mês
                                    <MaskedFieldGroup>
                                      <MaskedFieldUnit>R$</MaskedFieldUnit>
                                      <MaskedInput
                                        type="text"
                                        inputMode="decimal"
                                        autoFocus
                                        value={
                                          priceDrafts[feature.feature_id] || ''
                                        }
                                        onChange={e =>
                                          setPriceDrafts(current => ({
                                            ...current,
                                            [feature.feature_id]: currencyMask(
                                              e.target.value,
                                            ),
                                          }))
                                        }
                                      />
                                    </MaskedFieldGroup>
                                  </AddAddonLabel>
                                  <IconButton
                                    type="button"
                                    title="Confirmar"
                                    disabled={
                                      savingFeatureId === feature.feature_id
                                    }
                                    onClick={() =>
                                      handleConfirmBooleanGrant(
                                        feature.feature_id,
                                      )
                                    }
                                  >
                                    ✓
                                  </IconButton>
                                  <IconButton
                                    type="button"
                                    title="Cancelar"
                                    onClick={() =>
                                      setPendingGrant(current => ({
                                        ...current,
                                        [feature.feature_id]: false,
                                      }))
                                    }
                                  >
                                    <Icon
                                      name="close"
                                      size={16}
                                      color={colors.onSurfaceVariant}
                                    />
                                  </IconButton>
                                </ValueCell>
                              ) : (
                                <ToggleSwitch
                                  type="checkbox"
                                  title={
                                    plannedByPlan
                                      ? 'Já liberado pelo plano -- addon aqui não teria efeito'
                                      : undefined
                                  }
                                  disabled={
                                    !detail.can_grant_addons ||
                                    savingFeatureId === feature.feature_id ||
                                    plannedByPlan
                                  }
                                  checked={granted}
                                  onChange={e =>
                                    handleToggleBoolean(
                                      feature.feature_id,
                                      e.target.checked,
                                    )
                                  }
                                />
                              )}
                            </FeatureRow>
                          );
                        })}
                      </FeatureList>
                  </GroupSection>
                ))}
              </Panel>
            )}

            {activeTab === 'packages' && (
              <Panel>
                {numericFeatures.map(feature => {
                  const draft =
                    newAddonDrafts[feature.feature_id] ||
                    getDefaultDraft(feature);

                  return (
                    <AddAddonForm key={feature.feature_id}>
                      <FeatureInfo>
                        <FeatureName>{feature.nome}</FeatureName>
                        <FeatureMeta>
                          Plano:{' '}
                          {feature.plan_value === null
                            ? 'ilimitado'
                            : feature.plan_value}{' '}
                          · Efetivo:{' '}
                          {feature.effective_value === null
                            ? 'ilimitado'
                            : feature.effective_value}
                        </FeatureMeta>
                      </FeatureInfo>

                      <AddAddonLabel>
                        Quantidade
                        <MaskedInput
                          type="text"
                          inputMode="numeric"
                          disabled={!detail.can_grant_addons || draft.unlimited}
                          value={draft.unlimited ? '' : draft.value}
                          onChange={e =>
                            setNewAddonDrafts(current => ({
                              ...current,
                              [feature.feature_id]: {
                                ...draft,
                                value: onlyDigits(e.target.value),
                              },
                            }))
                          }
                        />
                      </AddAddonLabel>

                      <AddAddonLabel>
                        Preço/mês
                        <MaskedFieldGroup>
                          <MaskedFieldUnit>R$</MaskedFieldUnit>
                          <MaskedInput
                            type="text"
                            inputMode="decimal"
                            disabled={!detail.can_grant_addons}
                            value={draft.price}
                            onChange={e =>
                              setNewAddonDrafts(current => ({
                                ...current,
                                [feature.feature_id]: {
                                  ...draft,
                                  price: currencyMask(e.target.value),
                                },
                              }))
                            }
                          />
                        </MaskedFieldGroup>
                      </AddAddonLabel>

                      <UnlimitedLabel>
                        <input
                          type="checkbox"
                          disabled={!detail.can_grant_addons}
                          checked={draft.unlimited}
                          onChange={e =>
                            setNewAddonDrafts(current => ({
                              ...current,
                              [feature.feature_id]: {
                                ...draft,
                                unlimited: e.target.checked,
                              },
                            }))
                          }
                        />
                        Ilimitado
                      </UnlimitedLabel>

                      <IconButton
                        type="button"
                        title="Adicionar addon"
                        disabled={
                          !detail.can_grant_addons ||
                          savingFeatureId === feature.feature_id
                        }
                        onClick={() => handleAddAddon(feature.feature_id)}
                      >
                        ✓
                      </IconButton>
                    </AddAddonForm>
                  );
                })}

                <PackagesTable>
                  <PackagesTableHead>
                    <span>Pacote</span>
                    <span>Origem</span>
                    <span>Quantidade</span>
                    <span>Preço</span>
                    <span />
                  </PackagesTableHead>

                  {numericFeatures.map(feature => (
                    <React.Fragment key={feature.feature_id}>
                      <PackagesTableRow highlight>
                        <span>{feature.nome}</span>
                        <OriginBadge plan>Plano</OriginBadge>
                        <span>
                          {feature.plan_value === null
                            ? 'Ilimitado'
                            : feature.plan_value}
                        </span>
                        <span>—</span>
                        <span />
                      </PackagesTableRow>

                      {feature.addons.map(addon => (
                        <PackagesTableRow key={addon.id}>
                          <span>{feature.nome}</span>
                          <OriginBadge>Addon</OriginBadge>
                          <span>
                            {addon.value === null
                              ? 'Ilimitado'
                              : `+${addon.value}`}
                          </span>
                          <span>{formatPrice(addon.price)}</span>
                          <IconButton
                            type="button"
                            title="Remover addon"
                            disabled={
                              !detail.can_grant_addons ||
                              removingAddonId === addon.id
                            }
                            onClick={() => handleRemoveAddon(addon.id)}
                          >
                            <Icon
                              name="close"
                              size={16}
                              color={colors.onSurfaceVariant}
                            />
                          </IconButton>
                        </PackagesTableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </PackagesTable>
              </Panel>
            )}
          </>
        )}
      </Container>
    </Painel>
  );
};

export { GymFeatures };
